// 批量导入 — textarea 每行一域名,实时校验「已识别 N 个有效域名」,
// 解析并保存=逐行 POST jump-domains(非原子),汇总成功/失败。
import { useState } from 'react'
import { Button, useToast } from '@dogdisk/shared'
import {
  useCreateJumpDomain,
  type CreateDomainInput,
} from '../../_lib/jumpdomain-api'
import { CredentialSelect } from './credential-select'

// 简单域名校验:含点、无空格、合理字符。严格 RFC 由后端 DUPLICATE/格式 兜底。
const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i

function parseLines(raw: string): string[] {
  return raw
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
}

export function BatchImportTab({
  chainId,
  level,
  testidPrefix,
}: {
  chainId: string
  level: 1 | 2 | 3
  testidPrefix: string
}) {
  const [raw, setRaw] = useState('')
  const [busy, setBusy] = useState(false)
  const [credentialId, setCredentialId] = useState('')
  const create = useCreateJumpDomain()
  const toast = useToast()
  const showKey = level !== 3 // L3 自定义落地不绑 key

  const lines = parseLines(raw)
  const valid = lines.filter((l) => DOMAIN_RE.test(l))

  const onApply = async () => {
    if (valid.length === 0) return
    setBusy(true)
    let okN = 0
    const failed: string[] = []
    for (const domain of valid) {
      const input: CreateDomainInput = { chain_id: chainId, level, domain }
      if (credentialId) input.credential_id = credentialId
      try {
        await create.mutateAsync(input)
        okN += 1
      } catch {
        failed.push(domain)
      }
    }
    setBusy(false)
    setRaw(failed.join('\n')) // 失败行保留
    toast.push(`导入完成:成功 ${okN} / 失败 ${failed.length}`, {
      variant: failed.length === 0 ? 'success' : 'info',
    })
  }

  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-batch`}>
      {showKey && (
        <label className="flex items-center gap-2 text-xs text-gray-500">
          <span>批量绑定 API Key</span>
          <CredentialSelect
            value={credentialId}
            onChange={setCredentialId}
            testid={`${testidPrefix}-batch-cred`}
          />
        </label>
      )}
      <textarea
        className="h-28 w-full rounded border border-gray-300 p-2 font-mono text-sm"
        placeholder={'每行一个域名\nsub.example.com\ngo.example.net'}
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        data-testid={`${testidPrefix}-batch-textarea`}
      />
      <div className="flex items-center justify-between">
        <span className="text-xs text-green-600" data-testid={`${testidPrefix}-batch-count`}>
          已识别 {valid.length} 个有效域名
          {lines.length > valid.length && (
            <span className="ml-2 text-red-500">({lines.length - valid.length} 行格式无效)</span>
          )}
        </span>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => setRaw('')}>
            清空
          </Button>
          <Button
            size="sm"
            onClick={onApply}
            disabled={busy || valid.length === 0}
            data-testid={`${testidPrefix}-batch-apply`}
          >
            {busy ? '…' : '解析并保存'}
          </Button>
        </div>
      </div>
    </div>
  )
}
